// Copyright (c) 2025 Audere, All Rights Reserved

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Avatar, Box, Button, Menu, MenuItem, Typography, useMediaQuery } from "@mui/material";

import { selectCurrentName, selectCurrentPicture } from "../features/auth/authSlice";
import { useAppDispatch } from "../features/store";
import { createStyleSheet } from "../utils/styles";
import { theme } from "../../theme";
import { getFirstChar } from "../utils/string";
import { LogoutReasons } from "../types/logging";
import { apiSlice, logOutHelper } from "../features/api/apiSlice";

const UserProfile = () => {
  const { t } = useTranslation("UserProfile");
  const dispatch = useAppDispatch();
  const userName = useSelector(selectCurrentName);
  const picture = useSelector(selectCurrentPicture);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = useCallback(() => {
    handleMenuClose();
    logOutHelper(dispatch, apiSlice, LogoutReasons.UserInitiated);
  }, [dispatch]);

  return (
    <Box sx={styles.userContainer}>
      {picture ? (
        <Box
          component="img"
          src={picture}
          sx={{
            ...styles.avatar,
            cursor: isMobile ? "pointer" : "default",
          }}
          crossOrigin="anonymous"
          onClick={isMobile ? handleMenuOpen : undefined}
        />
      ) : (
        <Avatar
          sx={{
            ...styles.avatar,
            cursor: isMobile ? "pointer" : "default",
          }}
          onClick={isMobile ? handleMenuOpen : undefined}
        >
          {getFirstChar(userName)}
        </Avatar>
      )}

      {!isMobile && userName && (
        <>
          <Typography variant="body1" sx={styles.userName}>
            {userName}
          </Typography>
          <Button variant="outlined" onClick={handleLogout}>
            {t("signOut")}
          </Button>
        </>
      )}

      <Menu
        anchorEl={anchorEl}
        open={anchorEl != null}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleLogout}>{t("signOut")}</MenuItem>
      </Menu>
    </Box>
  );
};

const styles = createStyleSheet({
  userContainer: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
  },
  userName: {
    fontWeight: 500,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: "50px",
  },
  downArrow: {
    fontSize: "2rem",
    color: theme.palette.text.primary,
  },
  signOutButton: {
    color: "secondary",
    marginLeft: 2,
  },
});

export default UserProfile;
